// Cloud Whisper transcription (OpenAI API) for Qlipper Web Lite.
//
// Replaces the on-device transformers.js Whisper (engine/transcribe.js — accurate
// but SLOW on weak devices) with a fast cloud call. Audio is still extracted ON
// DEVICE (no video bytes leave the browser, only a tiny 16kHz mono WAV of the
// audio), then sent to the Whisper API. Returns the SAME normalized transcript
// shape as transcribeVideo() so engine/analyze.js consumes it unchanged.
//
//   video/audio Blob → { text, words:[{word,start,end}], segments:[{start,end,text}] }
//
// NOTE: audio extraction is inlined (not imported from transcribe.js) on purpose —
// transcribe.js pulls in @huggingface/transformers (~870 KB) for on-device Whisper,
// which Lite never uses. Keeping this self-contained keeps the Lite bundle lean.

// Decode any video/audio Blob to a 16kHz mono Float32 PCM buffer (WebAudio only).
async function extractAudio16kMono(blob) {
  const ab = await blob.arrayBuffer();
  const AC = window.AudioContext || window.webkitAudioContext;
  const ac = new AC();
  const decoded = await ac.decodeAudioData(ab.slice(0));
  try { ac.close(); } catch { /* noop */ }
  const targetRate = 16000;
  const frames = Math.ceil(decoded.duration * targetRate);
  const offline = new OfflineAudioContext(1, frames, targetRate);
  const src = offline.createBufferSource();
  src.buffer = decoded;
  src.connect(offline.destination);
  src.start();
  const rendered = await offline.startRendering();
  return { pcm: rendered.getChannelData(0), sampleRate: targetRate };
}

const WHISPER_ENDPOINT = 'https://api.openai.com/v1/audio/transcriptions';
const WHISPER_MODEL = 'whisper-1';
// 16kHz mono 16-bit WAV = 32 KB/s. OpenAI caps uploads at 25 MB ≈ ~13 min of audio.
const MAX_WAV_BYTES = 25 * 1024 * 1024;

// Encode a mono Float32 PCM buffer to a 16-bit PCM WAV Blob.
function encodeWav(float32, sampleRate) {
  const n = float32.length;
  const buf = new ArrayBuffer(44 + n * 2);
  const v = new DataView(buf);
  const str = (off, s) => { for (let i = 0; i < s.length; i++) v.setUint8(off + i, s.charCodeAt(i)); };
  str(0, 'RIFF'); v.setUint32(4, 36 + n * 2, true); str(8, 'WAVE');
  str(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true);   // PCM
  v.setUint16(22, 1, true);                                                // mono
  v.setUint32(24, sampleRate, true); v.setUint32(28, sampleRate * 2, true);
  v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  str(36, 'data'); v.setUint32(40, n * 2, true);
  let off = 44;
  for (let i = 0; i < n; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    v.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    off += 2;
  }
  return new Blob([buf], { type: 'audio/wav' });
}

/**
 * Transcribe a video/audio Blob via the OpenAI Whisper API.
 * @param {Blob} blob - the source video/audio file
 * @param {object} opts
 * @param {string} opts.openaiKey - OpenAI API key (BYO)
 * @param {string} [opts.language='id']
 * @param {(p:number)=>void} [opts.onProgress] - 0..1
 * @returns {Promise<{text:string, words:Array, segments:Array}>}
 */
export async function transcribeViaWhisperApi(blob, { openaiKey, language = 'id', onProgress } = {}) {
  if (!openaiKey) throw new Error('OpenAI API key required for transcription. Add it in Settings.');
  onProgress?.(0.05);

  // 1) extract 16kHz mono audio ON DEVICE (no video upload)
  const { pcm } = await extractAudio16kMono(blob);
  onProgress?.(0.35);

  const wav = encodeWav(pcm, 16000);
  if (wav.size > MAX_WAV_BYTES) {
    const mins = Math.floor((MAX_WAV_BYTES / 32000) / 60);
    throw new Error(`Audio too long for the Whisper API (>${mins} min). Trim the clip or upload a shorter file.`);
  }
  onProgress?.(0.45);

  // 2) send only the small audio WAV to Whisper
  const form = new FormData();
  form.append('file', new File([wav], 'audio.wav', { type: 'audio/wav' }));
  form.append('model', WHISPER_MODEL);
  form.append('response_format', 'verbose_json');
  form.append('timestamp_granularities[]', 'word');
  form.append('timestamp_granularities[]', 'segment');
  if (language) form.append('language', language);

  const res = await fetch(WHISPER_ENDPOINT, {
    method: 'POST',
    headers: { Authorization: `Bearer ${openaiKey}` },
    body: form,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Whisper API ${res.status}: ${body.slice(0, 200)}`);
  }
  const data = await res.json();
  onProgress?.(0.95);

  // 3) normalize to the transcript shape analyze.js expects
  const words = (data.words || [])
    .map((w) => ({ word: (w.word || '').trim(), start: w.start ?? 0, end: w.end ?? (w.start ?? 0) + 0.4 }))
    .filter((w) => w.word);

  let segments = (data.segments || [])
    .map((s) => ({ start: s.start ?? 0, end: s.end ?? 0, text: (s.text || '').trim() }))
    .filter((s) => s.text);

  // Fallback: if the API returned no segments, group words into ~10s blocks.
  if (!segments.length && words.length) {
    let cur = null;
    for (const w of words) {
      if (!cur || w.start - cur.start > 10) { cur = { start: w.start, end: w.end, text: w.word }; segments.push(cur); }
      else { cur.text += ' ' + w.word; cur.end = w.end; }
    }
  }
  onProgress?.(1);
  return { text: data.text || words.map((w) => w.word).join(' '), words, segments };
}

// Audio-capable model used for OpenRouter transcription. OpenRouter has no real
// Whisper endpoint, so we send the audio to a multimodal LLM and ask it to
// transcribe. No reliable WORD timestamps — only segment-level — so karaoke
// subtitles degrade gracefully (the renderer just skips them).
const OPENROUTER_AUDIO_MODEL = 'google/gemini-3.5-flash'; // GA, audio-capable (2.0-flash-001 retired/404)
const OPENROUTER_ENDPOINT = 'https://openrouter.ai/api/v1/chat/completions';

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

/**
 * Transcribe via OpenRouter (audio → multimodal LLM → JSON segments).
 * Same return shape as the Whisper path, but `words` is empty (no word timing).
 */
export async function transcribeViaOpenRouter(blob, { openrouterKey, model = OPENROUTER_AUDIO_MODEL, language = 'id', onProgress } = {}) {
  if (!openrouterKey) throw new Error('OpenRouter API key required for transcription. Add it in Settings.');
  onProgress?.(0.05);

  const { pcm } = await extractAudio16kMono(blob);
  onProgress?.(0.35);
  const wav = encodeWav(pcm, 16000);
  if (wav.size > MAX_WAV_BYTES) {
    throw new Error('Audio terlalu panjang buat di-transcribe sekaligus. Pakai file lebih pendek.');
  }
  const b64 = await blobToBase64(wav);
  onProgress?.(0.45);

  const lang = language === 'en' ? 'English' : 'Bahasa Indonesia';
  const sys = `You are an audio transcriber. Transcribe the audio in ${lang}. Return ONLY valid JSON (no markdown fence): {"segments":[{"start":<seconds number>,"end":<seconds number>,"text":"..."}]}. Split into natural ~5-10s segments with accurate start/end seconds.`;

  const res = await fetch(OPENROUTER_ENDPOINT, {
    method: 'POST',
    headers: { Authorization: `Bearer ${openrouterKey}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model,
      messages: [{
        role: 'user',
        content: [
          { type: 'text', text: sys },
          { type: 'input_audio', input_audio: { data: b64, format: 'wav' } },
        ],
      }],
    }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`OpenRouter ${res.status} (model ${model}): ${body.slice(0, 200)}`);
  }
  const data = await res.json();
  onProgress?.(0.9);

  const raw = data?.choices?.[0]?.message?.content || '';
  let parsed = null;
  try {
    const t = String(raw).trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '');
    const a = t.indexOf('{'), b = t.lastIndexOf('}');
    parsed = JSON.parse(b > a ? t.slice(a, b + 1) : t);
  } catch { /* fall through */ }

  const segments = (parsed?.segments || [])
    .map((s) => ({ start: Number(s.start) || 0, end: Number(s.end) || 0, text: (s.text || '').trim() }))
    .filter((s) => s.text);
  if (!segments.length) throw new Error('OpenRouter tidak mengembalikan transkrip yang bisa dibaca. Coba lagi atau pakai OpenAI Whisper.');

  onProgress?.(1);
  return { text: segments.map((s) => s.text).join(' '), words: [], segments };
}

// ── Zero-key path: same-origin proxy injects the server's OpenRouter key ──
// The browser never holds a key. /yt/or is the bridge route on app.weedlabs.online.
const OR_PROXY = '/yt/or';

// BYOK: when the user supplies their own API key it rides along as an X-Byok-Key
// header; the bridge forwards with it instead of the server key. Never persisted server-side.
let _byok = null; // { groq, openrouter } — a key per route (transcribe uses groq, analyze uses openrouter)
export function setByok(b) { _byok = b ? { groq: String(b.groq || '').trim(), openrouter: String(b.openrouter || '').trim() } : null; }
export function getByok() { return _byok; }
function byokHeaders(route) { const k = _byok && _byok[route]; return k ? { 'X-Byok-Key': k } : {}; }

export async function callOpenRouter(body) {
  const res = await fetch(OR_PROXY, {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...byokHeaders('openrouter') }, body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.error) {
    throw new Error(`AI server ${res.status}: ${(data.error?.message || data.error || JSON.stringify(data)).toString().slice(0, 200)}`);
  }
  return data;
}

// Transcribe with NO user key — audio extracted on-device, sent through the proxy.
const SAMPLE_RATE = 16000;
const CHUNK_SEC = 300; // 5-min chunks: each WAV ~9.6MB, comfortably under API limits

// Transcribe ONE audio chunk (a PCM slice) via the proxy; timestamps offset to
// the chunk's position in the full recording.
async function transcribeChunkViaProxy(pcmChunk, offsetSec, model, lang) {
  const b64 = await blobToBase64(encodeWav(pcmChunk, SAMPLE_RATE));
  const sys = `You are an audio transcriber. Transcribe the audio in ${lang}. Return ONLY valid JSON (no markdown fence): {"segments":[{"start":<seconds number>,"end":<seconds number>,"text":"..."}]}. Split into natural ~5-10s segments with accurate start/end seconds.`;
  const data = await callOpenRouter({
    model,
    messages: [{ role: 'user', content: [
      { type: 'text', text: sys },
      { type: 'input_audio', input_audio: { data: b64, format: 'wav' } },
    ] }],
  });
  let parsed = null;
  try {
    const t = String(data?.choices?.[0]?.message?.content || '').trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '');
    const a = t.indexOf('{'), b = t.lastIndexOf('}');
    parsed = JSON.parse(b > a ? t.slice(a, b + 1) : t);
  } catch { /* fall through */ }
  return (parsed?.segments || [])
    .map((s) => ({ start: (Number(s.start) || 0) + offsetSec, end: (Number(s.end) || 0) + offsetSec, text: (s.text || '').trim() }))
    .filter((s) => s.text);
}

// Zero-key transcription. AUTO-CHUNKS long audio (podcasts, webinars) into ~5-min
// parts so any length works — no more "audio too long". Chunks run sequentially;
// timestamps are stitched back to the full timeline.
export async function transcribeViaProxy(blob, { model = OPENROUTER_AUDIO_MODEL, language = 'id', onProgress } = {}) {
  onProgress?.(0.02);
  const { pcm } = await extractAudio16kMono(blob);
  onProgress?.(0.1);

  const lang = language === 'en' ? 'English' : 'Bahasa Indonesia';
  const chunkSamples = CHUNK_SEC * SAMPLE_RATE;
  const nChunks = Math.max(1, Math.ceil(pcm.length / chunkSamples));

  const segments = [];
  for (let i = 0; i < nChunks; i++) {
    const chunk = pcm.subarray(i * chunkSamples, Math.min(pcm.length, (i + 1) * chunkSamples));
    const offset = (i * chunkSamples) / SAMPLE_RATE;
    const segs = await transcribeChunkViaProxy(chunk, offset, model, lang);
    segments.push(...segs);
    onProgress?.(0.1 + 0.88 * ((i + 1) / nChunks));
  }
  if (!segments.length) throw new Error('Transkrip kosong / tidak terbaca. Coba video lain.');
  onProgress?.(1);
  return { text: segments.map((s) => s.text).join(' '), words: [], segments };
}

// ── Groq Whisper (real whisper-large-v3) via the /yt/groq proxy ──
// Returns WORD-level timestamps (karaoke works), cheaper + faster than the LLM
// approach. Key is injected server-side. Auto-chunks long audio like above.
const GROQ_PROXY = '/yt/groq';

async function groqChunk(pcmChunk, offsetSec, language) {
  const b64 = await blobToBase64(encodeWav(pcmChunk, SAMPLE_RATE));
  const res = await fetch(GROQ_PROXY, {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...byokHeaders('groq') },
    body: JSON.stringify({ audio_b64: b64, language }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.error) {
    throw new Error(`Transcribe ${res.status}: ${(data.error?.message || data.error || JSON.stringify(data)).toString().slice(0, 160)}`);
  }
  const words = (data.words || [])
    .map((w) => ({ word: (w.word || '').trim(), start: (Number(w.start) || 0) + offsetSec, end: (Number(w.end) || 0) + offsetSec }))
    .filter((w) => w.word);
  const segments = (data.segments || [])
    .map((s) => ({ start: (Number(s.start) || 0) + offsetSec, end: (Number(s.end) || 0) + offsetSec, text: (s.text || '').trim() }))
    .filter((s) => s.text);
  return { words, segments };
}

export async function transcribeViaGroqProxy(blob, { language = 'id', onProgress } = {}) {
  onProgress?.(0.02);
  const { pcm } = await extractAudio16kMono(blob);
  onProgress?.(0.1);
  const chunkSamples = CHUNK_SEC * SAMPLE_RATE;
  const nChunks = Math.max(1, Math.ceil(pcm.length / chunkSamples));
  const words = [], segments = [];
  for (let i = 0; i < nChunks; i++) {
    const chunk = pcm.subarray(i * chunkSamples, Math.min(pcm.length, (i + 1) * chunkSamples));
    const offset = (i * chunkSamples) / SAMPLE_RATE;
    const r = await groqChunk(chunk, offset, language);
    words.push(...r.words); segments.push(...r.segments);
    onProgress?.(0.1 + 0.88 * ((i + 1) / nChunks));
  }
  if (!segments.length && !words.length) throw new Error('Transkrip kosong / tidak terbaca. Coba video lain.');
  onProgress?.(1);
  return { text: (words.length ? words.map((w) => w.word).join(' ') : segments.map((s) => s.text).join(' ')), words, segments };
}
